import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Textarea,
} from '@fluentui/react-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { documentErrorMessage, type DocumentVersion } from './document-model';
import { useRefreshDocuments, useRestoreVersion } from './documents-api';
import { isStepUpRequired } from './model';
import { usePanelStyles } from './panel-styles';

/** A reason is mandatory: 3 to 500 characters once trimmed (the API's rule). */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

export interface RestoreDialogProps {
  spaceId: string;
  documentId: string;
  /** The version to restore: it becomes a new version with the next index. */
  version: DocumentVersion;
  onClose: () => void;
}

/** Asks why a previous version is restored, then adds it as a new version (history is kept). */
export function RestoreDialog({ spaceId, documentId, version, onClose }: RestoreDialogProps) {
  const { t } = useTranslation('documents');
  const styles = usePanelStyles();
  const notify = useNotify();
  const restore = useRestoreVersion();
  const refresh = useRefreshDocuments(spaceId);

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const trimmed = reason.trim();
  const reasonError = trimmed.length < REASON_MIN ? t('reason.required') : null;
  const index = version.revisionIndex ? isolate(version.revisionIndex) : '';

  const submit = async () => {
    setTouched(true);
    if (reasonError || restore.isPending) return;
    setFailure(null);
    try {
      const created = await restore.mutateAsync({
        spaceId,
        documentId,
        versionId: version.id,
        data: { reason: trimmed },
      });
      await refresh();
      notify({
        intent: 'success',
        message: t('restore.done', {
          index,
          newIndex: created.revisionIndex ? isolate(created.revisionIndex) : '',
        }),
      });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      setFailure(documentErrorMessage(t, error));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !restore.isPending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('restore.title', { index })}</DialogTitle>
          <DialogContent>
            <div className={styles.dialogBody}>
              <p>{t('restore.intro')}</p>
              <FormField
                label={t('reason.label')}
                required
                hint={t('reason.hint', { max: REASON_MAX })}
                error={touched ? reasonError : null}
              >
                <Textarea
                  className={styles.textarea}
                  maxLength={REASON_MAX}
                  value={reason}
                  onChange={(_event, data) => {
                    setReason(data.value);
                  }}
                  onBlur={() => {
                    setTouched(true);
                  }}
                />
              </FormField>
              {failure ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={restore.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={restore.isPending}
              onClick={() => {
                void submit();
              }}
            >
              {restore.isPending ? t('restore.restoring') : t('restore.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
